import { PROFILE_AXES, StyleProfile } from '../ShowStylePage/profile';

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string;

export interface ReviewItem {
  id: string;
  title: string;
  description: string;
}

interface ReviewValues {
  minColor?: string;
  maxColor?: string;
  minOg?: string;
  maxOg?: string;
  minFg?: string;
  maxFg?: string;
  minAbv?: string;
  maxAbv?: string;
  minIbu?: string;
  maxIbu?: string;
  minServingTemp?: string;
  maxServingTemp?: string;
  profile?: StyleProfile;
  guidelines?: any;
}

const filled = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== '';

const PARAMETER_PAIRS = [
  { name: 'OG', min: 'minOg', max: 'maxOg' },
  { name: 'FG', min: 'minFg', max: 'maxFg' },
  { name: 'ABV', min: 'minAbv', max: 'maxAbv' },
  { name: 'IBU', min: 'minIbu', max: 'maxIbu' },
  { name: 'Color', min: 'minColor', max: 'maxColor' },
] as const;

const TEXT_SECTIONS = [
  { field: 'summary', name: 'Overview' },
  { field: 'appearanceText', name: 'Appearance' },
  { field: 'aromaText', name: 'Aroma' },
  { field: 'flavorText', name: 'Flavor' },
  { field: 'bitternessText', name: 'Bitterness' },
  { field: 'mouthfeelText', name: 'Mouthfeel' },
] as const;

/**
 * What a person should look at after the style was generated: parameters the source did
 * not give, ranges that contradict themselves, texts and profile traits left empty.
 */
export const getReviewItems = (values: ReviewValues, t: Translate): ReviewItem[] => {
  const items: ReviewItem[] = [];
  const guidelines = values.guidelines ?? {};

  const missing = PARAMETER_PAIRS.filter(
    (pair) => !filled(values[pair.min]) || !filled(values[pair.max]),
  ).map((pair) => pair.name);

  if (missing.length > 0) {
    items.push({
      id: 'parameters-missing',
      title: t('Pages:StyleCreator.Review.ParametersMissing.Title', 'Parameters without a full range'),
      description: t(
        'Pages:StyleCreator.Review.ParametersMissing.Desc',
        'Minimum or maximum is missing for: {{list}}.',
        { list: missing.join(', ') },
      ),
    });
  }

  const inverted = PARAMETER_PAIRS.filter(
    (pair) =>
      filled(values[pair.min]) &&
      filled(values[pair.max]) &&
      Number(values[pair.min]) > Number(values[pair.max]),
  ).map((pair) => pair.name);

  if (inverted.length > 0) {
    items.push({
      id: 'parameters-inverted',
      title: t('Pages:StyleCreator.Review.ParametersInverted.Title', 'Minimum above maximum'),
      description: t(
        'Pages:StyleCreator.Review.ParametersInverted.Desc',
        'Check the values of: {{list}}.',
        { list: inverted.join(', ') },
      ),
    });
  }

  const emptyTexts = TEXT_SECTIONS.filter((section) => !filled(guidelines[section.field])).map(
    (section) => t(`Pages:StyleCreator.Review.Sections.${section.name}`, section.name),
  );

  if (emptyTexts.length > 0) {
    items.push({
      id: 'texts-missing',
      title: t('Pages:StyleCreator.Review.TextsMissing.Title', 'Guideline texts left empty'),
      description: t(
        'Pages:StyleCreator.Review.TextsMissing.Desc',
        'Nothing was found in the source for: {{list}}.',
        { list: emptyTexts.join(', ') },
      ),
    });
  }

  const unsetTraits = PROFILE_AXES.filter((axis) => !values.profile?.[axis]).map((axis) =>
    t(`Pages:StyleCreator.Review.Traits.${axis}`, axis),
  );

  if (unsetTraits.length > 0) {
    items.push({
      id: 'profile-unset',
      title: t('Pages:StyleCreator.Review.ProfileUnset.Title', 'Profile traits not set'),
      description: t(
        'Pages:StyleCreator.Review.ProfileUnset.Desc',
        'The source does not say enough about: {{list}}. They are hidden on the style page.',
        { list: unsetTraits.join(', ') },
      ),
    });
  }

  if (!filled(values.minServingTemp) && !filled(values.maxServingTemp)) {
    items.push({
      id: 'serving-temp',
      title: t('Pages:StyleCreator.Review.ServingTemp.Title', 'No serving temperature'),
      description: t(
        'Pages:StyleCreator.Review.ServingTemp.Desc',
        'The serving temperature was not found in the source.',
      ),
    });
  }

  const examples = guidelines.commercialExamples;
  const hasExamples = Array.isArray(examples) ? examples.length > 0 : filled(examples);

  if (!hasExamples) {
    items.push({
      id: 'examples',
      title: t('Pages:StyleCreator.Review.Examples.Title', 'No commercial examples'),
      description: t(
        'Pages:StyleCreator.Review.Examples.Desc',
        'Examples help judges and participants place the style.',
      ),
    });
  }

  return items;
};

/** How many of the fields the generation is expected to fill actually have a value. */
export const countFilledFields = (values: ReviewValues): { filled: number; total: number } => {
  const guidelines = values.guidelines ?? {};
  const parameters = PARAMETER_PAIRS.flatMap((pair) => [pair.min, pair.max]);

  const checks = [
    ...parameters.map((name) => filled(values[name])),
    ...TEXT_SECTIONS.map((section) => filled(guidelines[section.field])),
    ...PROFILE_AXES.map((axis) => Boolean(values.profile?.[axis])),
  ];

  return { filled: checks.filter(Boolean).length, total: checks.length };
};
